import { getAccessTokenExpiresAt } from '@/services/token-ttl';
import { z } from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const TokenDataSchema = z.object({
  userId: z.number(),
  email: z.string(),
  name: z.string(),
});

type TokenData = z.infer<typeof TokenDataSchema>;

type AuthStore = {
  accessToken: string | undefined;
  accessTokenData: TokenData | undefined;
  /** AccessToken 만료 시각: Unix ms (Date.now() + TTL). 타임존 무관, 비교용 */
  accessTokenExpiresAt: number | undefined;
};

type AuthActions = {
  setAccessToken: (accessToken: string | undefined) => void;
  /** 토큰 + 만료 시각 설정 (로그인/회원가입 시 사용) */
  setTokens: (accessToken: string) => void;
  /** 토큰 갱신 시 사용. accessToken만 갱신 */
  setTokensFromRefresh: (accessToken: string) => void;
  setAccessTokenData: (accessTokenData: TokenData | undefined) => void;
  init: () => void;
  clearTokens: () => void;
  isAccessTokenExpired: () => boolean;
};

const useAuthStore = create<AuthStore & AuthActions>()(
  persist(
    (set, get) => {
      return {
        accessToken: undefined,
        accessTokenData: undefined,
        accessTokenExpiresAt: undefined,

        setAccessTokenData: (accessTokenData: TokenData | undefined) => {
          set({ accessTokenData });
        },

        setAccessToken: (accessToken: string | undefined) => {
          set({ accessToken });
        },
        setTokens: (accessToken: string) => {
          set({
            accessToken,
            accessTokenExpiresAt: getAccessTokenExpiresAt(accessToken),
          });
        },
        setTokensFromRefresh: (accessToken: string) => {
          set({
            accessToken,
            accessTokenExpiresAt: getAccessTokenExpiresAt(accessToken),
          });
        },
        init: () => {},
        clearTokens: () => {
          set({
            accessToken: undefined,
            accessTokenData: undefined,
            accessTokenExpiresAt: undefined,
          });
        },
        isAccessTokenExpired: () => {
          const exp = get().accessTokenExpiresAt;
          return exp != null && Date.now() >= exp;
        },
      };
    },
    {
      name: 'auth-store',
    },
  ),
);

export { useAuthStore };
